import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

interface User {
  id: number;
  email: string;
  role: 'admin' | 'customer';
  name?: string;
  mobile_number?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zip_code?: string;
  quiet_time_start?: string;
  quiet_time_end?: string;
  quiet_timezone?: string;
  out_of_office?: boolean;
  agent_of_last_resort?: boolean;
  base_hourly_rate?: number | null;
  emergency_hourly_rate?: number | null;
  minimum_hours?: number | null;
  created_at: string;
}

interface Invitation {
  id: number;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
}

interface Agent {
  id: number;
  email: string;
  name?: string;
  quiet_time_start?: string;
  quiet_time_end?: string;
  quiet_timezone?: string;
  out_of_office?: boolean | number;
  agent_of_last_resort?: boolean | number;
}

interface TimeReportEntry {
  id: number;
  source: 'ticket' | 'task';
  source_id: number;
  source_title?: string | null;
  company_name?: string | null;
  start_time?: string;
  stop_time?: string;
  duration_minutes: number;
  notes?: string;
  created_at: string;
}

interface TimeReportAgent {
  user_id: number;
  email: string;
  name: string | null;
  minimum_hours?: number | null;
  total_minutes: number;
  ticket_minutes: number;
  task_minutes: number;
  entries: TimeReportEntry[];
}

const getDefaultReportDates = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    start: start.toISOString().slice(0, 10),
    end: now.toISOString().slice(0, 10),
  };
};

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const isGlobalAdmin = currentUser?.role === 'admin' && !currentUser?.company_id;
  const defaultDates = getDefaultReportDates();
  const [activeTab, setActiveTab] = useState<'users' | 'availability' | 'time-report'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  
  // Agent Availability state
  const [agents, setAgents] = useState<Agent[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState<string>('UTC');
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // Time Report state (Global Admin)
  const [timeReportStart, setTimeReportStart] = useState<string>(defaultDates.start);
  const [timeReportEnd, setTimeReportEnd] = useState<string>(defaultDates.end);
  const [timeReportData, setTimeReportData] = useState<TimeReportAgent[]>([]);
  const [timeReportLoading, setTimeReportLoading] = useState(false);
  const [timeReportFetched, setTimeReportFetched] = useState(false);
  const [expandedTimeReportUsers, setExpandedTimeReportUsers] = useState<Set<number>>(new Set());

  const toggleTimeReportUserExpanded = (userId: number) => {
    setExpandedTimeReportUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'customer' as 'admin' | 'customer',
    mobile_number: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    quiet_time_start: '',
    quiet_time_end: '',
    quiet_timezone: '',
    out_of_office: false,
    agent_of_last_resort: false,
    base_hourly_rate: '',
    emergency_hourly_rate: '',
    minimum_hours: '',
  });
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    role: 'customer' as 'admin' | 'customer',
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchInvitations();
  }, []);

  useEffect(() => {
    if (activeTab === 'availability') {
      fetchAgents();
    }
  }, [activeTab]);

  const fetchTimeReport = async () => {
    if (!timeReportStart || !timeReportEnd) return;
    setTimeReportLoading(true);
    setTimeReportFetched(false);
    try {
      const response = await api.get('/users/time-report', {
        params: { start_date: timeReportStart, end_date: timeReportEnd },
      });
      setTimeReportData(response.data.agents || []);
      setTimeReportFetched(true);
    } catch (error: any) {
      console.error('Error fetching time report:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error loading time report: ${message}`);
    } finally {
      setTimeReportLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'time-report' && timeReportStart && timeReportEnd) {
      fetchTimeReport();
    }
  }, [activeTab, timeReportStart, timeReportEnd]);

  useEffect(() => {
    // Update current time every minute
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error fetching users: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await api.get('/users/invitations');
      setInvitations(response.data);
    } catch (error: any) {
      console.error('Error fetching invitations:', error);
      // Don't alert here to avoid double alerts on page load if both fail
    }
  };

  const fetchAgents = async () => {
    setAvailabilityLoading(true);
    try {
      const response = await api.get('/users/agents/availability');
      setAgents(response.data);
    } catch (error: any) {
      console.error('Error fetching agents:', error);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({ 
      email: '', 
      password: '', 
      name: '', 
      role: 'customer',
      mobile_number: '',
      address: '',
      city: '',
      state: '',
      country: '',
      zip_code: '',
      quiet_time_start: '',
      quiet_time_end: '',
      quiet_timezone: '',
      out_of_office: false,
      agent_of_last_resort: false,
      base_hourly_rate: '',
      emergency_hourly_rate: '',
      minimum_hours: '',
    });
    setShowModal(true);
  };

  const handleInvite = () => {
    setInviteFormData({ email: '', role: 'customer' });
    setShowInviteModal(true);
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteLoading(true);
    try {
      await api.post('/users/invite', inviteFormData);
      setShowInviteModal(false);
      setInviteFormData({ email: '', role: 'customer' });
      fetchInvitations();
      alert('Invitation sent successfully!');
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error sending invitation: ${message}`);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvite = async (id: number) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }
    try {
      await api.delete(`/users/invitations/${id}`);
      fetchInvitations();
    } catch (error: any) {
      console.error('Error canceling invitation:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error canceling invitation: ${message}`);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({ 
      email: user.email, 
      password: '', 
      name: user.name || '', 
      role: user.role,
      mobile_number: user.mobile_number || '',
      address: user.address || '',
      city: user.city || '',
      state: user.state || '',
      country: user.country || '',
      zip_code: user.zip_code || '',
      quiet_time_start: user.quiet_time_start || '',
      quiet_time_end: user.quiet_time_end || '',
      quiet_timezone: user.quiet_timezone || '',
      out_of_office: user.out_of_office || false,
      agent_of_last_resort: user.agent_of_last_resort || false,
      base_hourly_rate: user.base_hourly_rate != null ? String(user.base_hourly_rate) : '',
      emergency_hourly_rate: user.emergency_hourly_rate != null ? String(user.emergency_hourly_rate) : '',
      minimum_hours: user.minimum_hours != null ? String(user.minimum_hours) : '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await api.delete(`/users/${id}`);
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error deleting user: ${message}`);
    }
  };

  const handleResetPassword = (userId: number) => {
    setResetPasswordUserId(userId);
    setResetPasswordValue('');
    setShowResetPasswordModal(true);
  };

  const handleSendPasswordReset = async (userId: number) => {
    if (!window.confirm('Send password reset email to this user?')) {
      return;
    }

    try {
      await api.post(`/users/${userId}/send-password-reset`);
      alert('Password reset email sent successfully!');
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error sending password reset email: ${message}`);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPasswordUserId || !resetPasswordValue) {
      alert('Please enter a password');
      return;
    }

    if (resetPasswordValue.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setResetPasswordLoading(true);
    try {
      await api.post(`/users/${resetPasswordUserId}/reset-password`, { password: resetPasswordValue });
      setShowResetPasswordModal(false);
      setResetPasswordUserId(null);
      setResetPasswordValue('');
      alert('Password reset successfully!');
    } catch (error: any) {
      console.error('Error resetting password:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error resetting password: ${message}`);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user - only send fields that are provided
        const updateData: any = { 
          email: formData.email, 
          role: formData.role,
          name: formData.name || null,
          mobile_number: formData.mobile_number || null,
          address: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          country: formData.country || null,
          zip_code: formData.zip_code || null,
          quiet_time_start: formData.quiet_time_start || null,
          quiet_time_end: formData.quiet_time_end || null,
          quiet_timezone: formData.quiet_timezone || null,
          out_of_office: formData.out_of_office,
          agent_of_last_resort: formData.agent_of_last_resort,
          base_hourly_rate: formData.base_hourly_rate !== '' ? parseFloat(formData.base_hourly_rate) : null,
          emergency_hourly_rate: formData.emergency_hourly_rate !== '' ? parseFloat(formData.emergency_hourly_rate) : null,
          minimum_hours: formData.minimum_hours !== '' ? parseFloat(formData.minimum_hours) : null,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/users/${editingUser.id}`, updateData);
      } else {
        // Create user
        const createPayload = {
          ...formData,
          base_hourly_rate: formData.base_hourly_rate !== '' ? parseFloat(formData.base_hourly_rate) : undefined,
          emergency_hourly_rate: formData.emergency_hourly_rate !== '' ? parseFloat(formData.emergency_hourly_rate) : undefined,
          minimum_hours: formData.minimum_hours !== '' ? parseFloat(formData.minimum_hours) : undefined,
        };
        await api.post('/users', createPayload);
      }
      setShowModal(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error saving user: ${message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Agent Availability helper functions
  const getAvailableTimezones = (): string[] => {
    const timezones = new Set<string>();
    agents.forEach(agent => {
      if (agent.quiet_timezone) {
        timezones.add(agent.quiet_timezone);
      }
    });
    return Array.from(timezones).sort();
  };

  const convertHourToTimezone = (hour: number, fromTimezone: string, toTimezone: string): number => {
    try {
      if (fromTimezone === toTimezone) {
        return hour;
      }

      const now = new Date();
      const fromFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: fromTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      const fromParts = fromFormatter.formatToParts(now);
      const year = parseInt(fromParts.find(p => p.type === 'year')?.value || '0', 10);
      const month = parseInt(fromParts.find(p => p.type === 'month')?.value || '0', 10) - 1;
      const day = parseInt(fromParts.find(p => p.type === 'day')?.value || '0', 10);
      const currentHour = parseInt(fromParts.find(p => p.type === 'hour')?.value || '0', 10);
      
      const testDate = new Date();
      testDate.setFullYear(year, month, day);
      testDate.setHours(hour, 0, 0, 0);
      
      const checkFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: fromTimezone,
        hour: '2-digit',
        hour12: false
      });
      
      let testHour = parseInt(checkFormatter.formatToParts(testDate).find(p => p.type === 'hour')?.value || '0', 10);
      let diff = hour - testHour;
      
      testDate.setHours(testDate.getHours() + diff);
      
      testHour = parseInt(checkFormatter.formatToParts(testDate).find(p => p.type === 'hour')?.value || '0', 10);
      if (testHour !== hour) {
        diff = hour - testHour;
        testDate.setHours(testDate.getHours() + diff);
      }
      
      const toFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: toTimezone,
        hour: '2-digit',
        hour12: false
      });
      
      const toParts = toFormatter.formatToParts(testDate);
      return parseInt(toParts.find(p => p.type === 'hour')?.value || '0', 10);
    } catch (error) {
      console.error('Error converting timezone:', error);
      return hour;
    }
  };

  const isAgentAvailableAtHour = (agent: Agent, hour: number, viewTimezone: string): boolean => {
    if (agent.out_of_office === 1 || agent.out_of_office === true) {
      return false;
    }

    if (!agent.quiet_time_start || !agent.quiet_time_end || !agent.quiet_timezone) {
      return true;
    }

    try {
      const agentTimezone = agent.quiet_timezone;
      const hourInAgentTimezone = convertHourToTimezone(hour, viewTimezone, agentTimezone);
      
      const [startHour, startMinute] = agent.quiet_time_start.split(':').map(Number);
      const [endHour, endMinute] = agent.quiet_time_end.split(':').map(Number);
      const startTimeMinutes = startHour * 60 + startMinute;
      const endTimeMinutes = endHour * 60 + endMinute;
      const checkTimeMinutes = hourInAgentTimezone * 60;

      if (startTimeMinutes > endTimeMinutes) {
        if (checkTimeMinutes >= startTimeMinutes || checkTimeMinutes <= endTimeMinutes) {
          return false;
        }
      } else {
        if (checkTimeMinutes >= startTimeMinutes && checkTimeMinutes <= endTimeMinutes) {
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Error checking agent availability:', error);
      return true;
    }
  };

  const formatHour = (hour: number): string => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };

  const getCurrentHourInTimezone = (timezone: string): number => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        hour12: false
      });
      const parts = formatter.formatToParts(currentTime);
      return parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    } catch (error) {
      return 0;
    }
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Global Users</h1>
        {activeTab === 'users' && (
          <div className="flex space-x-3">
            <button
              onClick={handleInvite}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 dark:bg-green-500 hover:bg-green-700 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Invite User
            </button>
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add User
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => {
              setActiveTab('availability');
              if (agents.length === 0) {
                fetchAgents();
              }
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'availability'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Agent Availability
          </button>
          {isGlobalAdmin && (
            <button
              onClick={() => setActiveTab('time-report')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'time-report'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Time Report
            </button>
          )}
        </nav>
      </div>

      {activeTab === 'time-report' ? (
        /* Time Report Tab (Global Admin) */
        <div>
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div>
              <label htmlFor="time-report-start" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                From
              </label>
              <input
                id="time-report-start"
                type="date"
                value={timeReportStart}
                onChange={(e) => setTimeReportStart(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="time-report-end" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                To
              </label>
              <input
                id="time-report-end"
                type="date"
                value={timeReportEnd}
                onChange={(e) => setTimeReportEnd(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <button
              type="button"
              onClick={fetchTimeReport}
              disabled={timeReportLoading}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {timeReportLoading ? 'Loading…' : 'Apply'}
            </button>
          </div>
          {timeReportLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Time tracking per agent for the selected period. Includes ticket and internal project task time entries.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="w-10 px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"></th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Agent</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ticket (hrs)</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Task (hrs)</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total (hrs)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {!timeReportFetched ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          Set date range and click Apply to load the report.
                        </td>
                      </tr>
                    ) : timeReportData.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          No time entries in this period.
                        </td>
                      </tr>
                    ) : (
                      timeReportData.map((row) => {
                        const totalHours = row.total_minutes / 60;
                        const overMinimum = row.minimum_hours != null && totalHours > row.minimum_hours;
                        const nearingMinimum = row.minimum_hours != null && totalHours >= row.minimum_hours * 0.9 && totalHours < row.minimum_hours;
                        const rowBgClass = overMinimum
                          ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
                          : nearingMinimum
                            ? 'bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700';
                        return (
                        <React.Fragment key={row.user_id}>
                          <tr className={rowBgClass}>
                            <td className="w-10 px-2 py-4 whitespace-nowrap">
                              {row.entries.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => toggleTimeReportUserExpanded(row.user_id)}
                                  className="p-1 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  aria-expanded={expandedTimeReportUsers.has(row.user_id)}
                                  title={expandedTimeReportUsers.has(row.user_id) ? 'Collapse' : 'Expand'}
                                >
                                  <svg
                                    className={`w-5 h-5 transition-transform ${expandedTimeReportUsers.has(row.user_id) ? 'rotate-90' : ''}`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              ) : (
                                <span className="w-5 h-5 inline-block" />
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {row.name ? `${row.name} (${row.email})` : row.email}
                                {nearingMinimum && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200" title={`Nearing minimum (${row.minimum_hours} hrs)`}>
                                    Nearing Minimum
                                  </span>
                                )}
                                {overMinimum && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200" title={`Over minimum (${row.minimum_hours} hrs)`}>
                                    Over minimum
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700 dark:text-gray-300">
                              {(row.ticket_minutes / 60).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700 dark:text-gray-300">
                              {(row.task_minutes / 60).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                              {(row.total_minutes / 60).toFixed(2)}
                            </td>
                          </tr>
                          {expandedTimeReportUsers.has(row.user_id) && row.entries.length > 0 && (
                            <tr className="bg-gray-50 dark:bg-gray-900/50">
                              <td colSpan={5} className="px-6 py-4">
                                <div className="ml-6 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                                    <thead className="bg-gray-100 dark:bg-gray-700">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Source</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Reference</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Title</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Company</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Duration</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Notes</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                      {row.entries.map((entry) => (
                                        <tr key={`${entry.source}-${entry.id}`} className="text-sm">
                                          <td className="px-4 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
                                            {entry.source === 'ticket' ? 'Ticket' : 'Task'}
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap">
                                            {entry.source === 'ticket' ? (
                                              <a href={`/tickets/${entry.source_id}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                                                #{entry.source_id}
                                              </a>
                                            ) : (
                                              <span className="text-gray-700 dark:text-gray-300">#{entry.source_id}</span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={entry.source_title || ''}>
                                            {entry.source_title || '—'}
                                          </td>
                                          <td className="px-4 py-2 text-gray-600 dark:text-gray-400 max-w-[160px] truncate" title={entry.company_name || ''}>
                                            {entry.company_name || '—'}
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap text-gray-600 dark:text-gray-400">
                                            {(entry.start_time ? new Date(entry.start_time) : new Date(entry.created_at)).toLocaleString()}
                                          </td>
                                          <td className="px-4 py-2 text-right whitespace-nowrap text-gray-700 dark:text-gray-300">
                                            {(entry.duration_minutes / 60).toFixed(2)} h
                                          </td>
                                          <td className="px-4 py-2 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={entry.notes || ''}>
                                            {entry.notes || '—'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                    )}
                  </tbody>
                </table>
              </div>
              {timeReportFetched && timeReportData.length > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Total: {timeReportData.reduce((sum, r) => sum + r.total_minutes, 0) / 60} hours across {timeReportData.length} agent(s).
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : activeTab === 'availability' ? (
        /* Agent Availability Tab */
        <div>
          <div className="mb-6 flex justify-between items-center">
            {getAvailableTimezones().length > 0 && (
              <div className="flex items-center gap-2">
                <label htmlFor="timezone-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  View Timezone:
                </label>
                <select
                  id="timezone-select"
                  value={selectedTimezone}
                  onChange={(e) => setSelectedTimezone(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="UTC">UTC</option>
                  {getAvailableTimezones().map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {availabilityLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This view shows agent availability over a 24-hour period based on their quiet hours settings.
                  Green indicates available, red indicates quiet hours, and gray indicates out of office.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-700 z-10">
                        Agent
                      </th>
                      {Array.from({ length: 24 }, (_, i) => i).map(hour => {
                        const currentHour = getCurrentHourInTimezone(selectedTimezone);
                        return (
                          <th
                            key={hour}
                            className={`px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider ${
                              hour === currentHour ? 'bg-indigo-100 dark:bg-indigo-900' : ''
                            }`}
                          >
                            <div className="flex flex-col">
                              <span>{formatHour(hour)}</span>
                              {hour === currentHour && (
                                <span className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">●</span>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {agents.length === 0 ? (
                      <tr>
                        <td colSpan={25} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          No agents found
                        </td>
                      </tr>
                    ) : (
                      agents.map(agent => {
                        const agentName = agent.name || agent.email;
                        const isOutOfOffice = agent.out_of_office === 1 || agent.out_of_office === true;
                        const hasQuietHours = !!(agent.quiet_time_start && agent.quiet_time_end && agent.quiet_timezone);
                        const currentHour = getCurrentHourInTimezone(selectedTimezone);

                        return (
                          <tr key={agent.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 z-10">
                              <div className="flex flex-col">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {agent.name ? `${agent.name} (${agent.email})` : agent.email}
                                </div>
                                {isOutOfOffice && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 mt-1">
                                    Out of Office
                                  </span>
                                )}
                                {hasQuietHours && !isOutOfOffice && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {agent.quiet_time_start} - {agent.quiet_time_end} ({agent.quiet_timezone})
                                  </span>
                                )}
                                {!hasQuietHours && !isOutOfOffice && (
                                  <span className="text-xs text-green-600 dark:text-green-400 mt-1">
                                    Always Available
                                  </span>
                                )}
                              </div>
                            </td>
                            {Array.from({ length: 24 }, (_, i) => i).map(hour => {
                              const isAvailable = isAgentAvailableAtHour(agent, hour, selectedTimezone);
                              const isCurrentHour = hour === currentHour;

                              return (
                                <td
                                  key={hour}
                                  className={`px-2 py-4 text-center ${
                                    isCurrentHour ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                                  }`}
                                >
                                  <div
                                    className={`h-8 w-full rounded ${
                                      isOutOfOffice
                                        ? 'bg-gray-300 dark:bg-gray-600'
                                        : isAvailable
                                        ? 'bg-green-400 dark:bg-green-600'
                                        : 'bg-red-400 dark:bg-red-600'
                                    } ${
                                      isCurrentHour ? 'ring-2 ring-indigo-500 dark:ring-indigo-400' : ''
                                    }`}
                                    title={`${formatHour(hour)}: ${isOutOfOffice ? 'Out of Office' : isAvailable ? 'Available' : 'Quiet Hours'}`}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-400 dark:bg-green-600"></div>
                    <span className="text-gray-700 dark:text-gray-300">Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400 dark:bg-red-600"></div>
                    <span className="text-gray-700 dark:text-gray-300">Quiet Hours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-gray-300 dark:bg-gray-600"></div>
                    <span className="text-gray-700 dark:text-gray-300">Out of Office</span>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-gray-500 dark:text-gray-400">●</span>
                    <span className="text-gray-700 dark:text-gray-300">Current Hour</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Users Tab */
        <>

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md mb-10">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Active Global Users</h3>
        </div>
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {users.map((user) => (
            <li key={user.id}>
              <div className="px-4 py-4 sm:px-6 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400 truncate">
                      {user.name ? `${user.name} (${user.email})` : user.email}
                    </p>
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'admin' 
                        ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    }`}>
                      {user.role === 'admin' ? 'Global Admin' : 'Global User'}
                    </span>
                    {currentUser?.id === user.id && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        You
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    <p>Created: {new Date(user.created_at).toLocaleString()}</p>
                    {user.name && <p>Name: {user.name}</p>}
                    {user.mobile_number && <p>Mobile: {user.mobile_number}</p>}
                    {(user.city || user.state || user.country) && (
                      <p>
                        {[user.city, user.state, user.country].filter(Boolean).join(', ')}
                        {user.zip_code && ` ${user.zip_code}`}
                      </p>
                    )}
                    {user.out_of_office && (
                      <p className="text-orange-600 dark:text-orange-400 font-medium">Out of Office</p>
                    )}
                    {user.agent_of_last_resort && (
                      <p className="text-blue-600 dark:text-blue-400 font-medium">Agent of Last Resort</p>
                    )}
                    {(user.quiet_time_start || user.quiet_time_end) && (
                      <p className="text-sm">
                        Quiet Time: {user.quiet_time_start || '--'} - {user.quiet_time_end || '--'}
                        {user.quiet_timezone && ` (${user.quiet_timezone})`}
                      </p>
                    )}
                    {(user.base_hourly_rate != null || user.emergency_hourly_rate != null || user.minimum_hours != null) && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {user.base_hourly_rate != null && `Base: ${user.base_hourly_rate}/hr`}
                        {user.base_hourly_rate != null && (user.emergency_hourly_rate != null || user.minimum_hours != null) && ' · '}
                        {user.emergency_hourly_rate != null && `Emergency: ${user.emergency_hourly_rate}/hr`}
                        {(user.base_hourly_rate != null || user.emergency_hourly_rate != null) && user.minimum_hours != null && ' · '}
                        {user.minimum_hours != null && `Min: ${user.minimum_hours}h`}
                      </p>
                    )}
                  </div>
                </div>
                <div className="ml-4 flex space-x-2">
                  <button
                    onClick={() => handleEdit(user)}
                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 text-sm font-medium"
                  >
                    Edit
                  </button>
                  {!currentUser?.company_id && (
                    <>
                      <button
                        onClick={() => handleResetPassword(user.id)}
                        className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 text-sm font-medium"
                        title="Set password directly"
                      >
                        Reset Password
                      </button>
                      <button
                        onClick={() => handleSendPasswordReset(user.id)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 text-sm font-medium"
                        title="Send password reset email"
                      >
                        Send Reset Email
                      </button>
                    </>
                  )}
                  {currentUser?.id !== user.id && (
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 text-sm font-medium"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
          {users.length === 0 && (
            <li className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No active global users found</li>
          )}
        </ul>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pending Global Invitations</h3>
        </div>
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {invitations.map((inv) => (
            <li key={inv.id}>
              <div className="px-4 py-4 sm:px-6 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400 truncate">
                      {inv.email}
                    </p>
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                      {inv.role === 'admin' ? 'Global Admin' : 'Global User'}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-col sm:flex-row sm:space-x-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Sent: {new Date(inv.created_at).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      Expires: {new Date(inv.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="ml-4 flex space-x-2">
                  <button
                    onClick={() => {
                      setInviteFormData({ email: inv.email, role: inv.role as 'admin' | 'customer' });
                      setShowInviteModal(true);
                    }}
                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 text-sm font-medium"
                  >
                    Resend
                  </button>
                  <button
                    onClick={() => handleCancelInvite(inv.id)}
                    className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </li>
          ))}
          {invitations.length === 0 && (
            <li className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No pending global invitations found</li>
          )}
        </ul>
      </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {editingUser ? 'Edit User' : 'Add User'}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                      <input
                        type="text"
                        required={!editingUser}
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                      <input
                        type="email"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Password {editingUser && '(leave blank to keep current)'}
                      </label>
                      <input
                        type="password"
                        required={!editingUser}
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'customer' })}
                      >
                        <option value="customer">Global User</option>
                        <option value="admin">Global Admin</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mobile Number</label>
                      <input
                        type="tel"
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.mobile_number}
                        onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
                        <input
                          type="text"
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">State</label>
                        <input
                          type="text"
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          value={formData.state}
                          onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                          placeholder="State"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Country</label>
                        <input
                          type="text"
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                          placeholder="Country"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Zip Code</label>
                        <input
                          type="text"
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          value={formData.zip_code}
                          onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                          placeholder="12345"
                        />
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Rates & Minimum Hours</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Base Hourly Rate</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            value={formData.base_hourly_rate}
                            onChange={(e) => setFormData({ ...formData, base_hourly_rate: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Emergency Hourly Rate</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            value={formData.emergency_hourly_rate}
                            onChange={(e) => setFormData({ ...formData, emergency_hourly_rate: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Minimum Hours</label>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            value={formData.minimum_hours}
                            onChange={(e) => setFormData({ ...formData, minimum_hours: e.target.value })}
                            placeholder="Hours"
                          />
                        </div>
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Availability Settings</h4>
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center">
                          <input
                            id="out_of_office"
                            type="checkbox"
                            checked={formData.out_of_office}
                            onChange={(e) => setFormData({ ...formData, out_of_office: e.target.checked })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor="out_of_office" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Out of Office
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="agent_of_last_resort"
                            type="checkbox"
                            checked={formData.agent_of_last_resort}
                            onChange={(e) => setFormData({ ...formData, agent_of_last_resort: e.target.checked })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor="agent_of_last_resort" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Agent of Last Resort
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                          Agent of Last Resort will be contacted if all other agents fail to acknowledge, regardless of quiet time or out of office settings.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Quiet Time Start</label>
                          <input
                            type="time"
                            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            value={formData.quiet_time_start}
                            onChange={(e) => setFormData({ ...formData, quiet_time_start: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Quiet Time End</label>
                          <input
                            type="time"
                            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            value={formData.quiet_time_end}
                            onChange={(e) => setFormData({ ...formData, quiet_time_end: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Time Zone</label>
                          <select
                            className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            value={formData.quiet_timezone}
                            onChange={(e) => setFormData({ ...formData, quiet_timezone: e.target.value })}
                          >
                            <option value="">Select timezone...</option>
                            <option value="America/New_York">Eastern Time (ET)</option>
                            <option value="America/Chicago">Central Time (CT)</option>
                            <option value="America/Denver">Mountain Time (MT)</option>
                            <option value="America/Los_Angeles">Pacific Time (PT)</option>
                            <option value="America/Anchorage">Alaska Time (AKT)</option>
                            <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
                            <option value="UTC">UTC</option>
                            <option value="Europe/London">London (GMT/BST)</option>
                            <option value="Europe/Paris">Paris (CET/CEST)</option>
                            <option value="Europe/Berlin">Berlin (CET/CEST)</option>
                            <option value="Asia/Tokyo">Tokyo (JST)</option>
                            <option value="Asia/Shanghai">Shanghai (CST)</option>
                            <option value="Asia/Dubai">Dubai (GST)</option>
                            <option value="Australia/Sydney">Sydney (AEDT/AEST)</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Quiet time can span midnight (e.g., 22:00 to 08:00).
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-base font-medium text-white hover:bg-indigo-700 dark:hover:bg-indigo-600 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowInviteModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleInviteSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Invite Global User
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
                      <input
                        type="email"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={inviteFormData.email}
                        onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
                        placeholder="user@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={inviteFormData.role}
                        onChange={(e) => setInviteFormData({ ...inviteFormData, role: e.target.value as 'admin' | 'customer' })}
                      >
                        <option value="customer">Global User</option>
                        <option value="admin">Global Admin</option>
                      </select>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        An invitation email will be sent to the user with a link to create their account.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 dark:bg-green-500 text-base font-medium text-white hover:bg-green-700 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {inviteLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Send Invitation'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowResetPasswordModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleResetPasswordSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Reset User Password
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Password</label>
                      <input
                        type="password"
                        required
                        minLength={6}
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={resetPasswordValue}
                        onChange={(e) => setResetPasswordValue(e.target.value)}
                        placeholder="Enter new password (min 6 characters)"
                      />
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        This will immediately change the user's password. They will need to use this new password to log in.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={resetPasswordLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 dark:bg-green-500 text-base font-medium text-white hover:bg-green-700 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setResetPasswordUserId(null);
                      setResetPasswordValue('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
